import React, { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { GridOutline, Options2Outline } from '@lyearn/icons';
import { Box, Checkbox, Stack, Typography } from '@lyearn/ui';
import RadioGroup from '@/components/RadioGroup';
import { RadioOption } from '@/components/RadioGroup/types';
import { PropertiesToDuplicateEnum } from '@/types/schema';

import { getPropertiesToDuplicateOptions } from '../../utils';

interface PropertiesToDuplicateSelectorProps {
  value: PropertiesToDuplicateEnum[];
  onChange: (value: PropertiesToDuplicateEnum[]) => void;
}

enum RadioEnum {
  EVERYTHING = 'everything',
  CUSTOM = 'custom',
}

function PropertiesToDuplicateSelector({ value, onChange }: PropertiesToDuplicateSelectorProps) {
  const { t } = useTranslation();
  const [radioSelection, setRadioSelection] = useState<RadioEnum>(RadioEnum.EVERYTHING);

  const radioOptions = useMemo(
    () => [
      {
        id: RadioEnum.EVERYTHING,
        label: t('Task:DuplicationProperties.Everything'),
        Icon: GridOutline,
      },
      {
        id: RadioEnum.CUSTOM,
        label: t('Task:DuplicationProperties.Custom'),
        Icon: Options2Outline,
      },
    ],
    [t],
  );

  const options = useMemo(() => getPropertiesToDuplicateOptions(t), [t]);

  const handleRadioChange = useCallback(
    (_: string, changedOption: RadioOption<RadioEnum>) => {
      if (changedOption.id === RadioEnum.EVERYTHING) {
        onChange(options.map((option) => option.value));
      }
      setRadioSelection(changedOption.id);
    },
    [options, onChange],
  );

  const toggleProperty = useCallback(
    (property: PropertiesToDuplicateEnum) => {
      onChange(
        value.includes(property) ? value.filter((p) => p !== property) : [...value, property],
      );
    },
    [value, onChange],
  );

  const handleSelectAllClick = useCallback(() => {
    if (value.length === 0) {
      onChange(options.map((option) => option.value));
    } else {
      onChange([]);
    }
  }, [value, options, onChange]);

  return (
    <Stack className="w-full" direction="col" gapY={32}>
      <RadioGroup<RadioEnum>
        className="w-full grid grid-cols-2"
        direction="row"
        gap={12}
        options={radioOptions}
        selectedId={radioSelection}
        type="box"
        onChange={handleRadioChange}
      />

      {radioSelection === RadioEnum.EVERYTHING ? (
        <Typography className="ms-8" variant="body-short-01">
          {t('Task:DuplicateTask.EverythingDescription')}
        </Typography>
      ) : (
        <Stack className="w-full" direction="col" gapY={24}>
          <Typography className="ms-8" variant="body-short-01">
            {t('Task:DuplicationProperties.CustomDescription')}
          </Typography>

          <Stack className="w-full border border-border rounded-8" direction="col">
            <Stack
              alignItems="center"
              className="w-full py-12 ps-18 pe-8 border-b border-border cursor-pointer"
              direction="row"
              justifyContent="between"
              role="button"
              onClick={handleSelectAllClick}>
              <Typography variant="label-02">{t('Common:Filter.SelectAll')}</Typography>
              <Checkbox
                checked={options.length === value.length}
                indeterminate={value.length > 0 && value.length < options.length}
              />
            </Stack>

            <Box className="w-full p-16 grid grid-cols-2 gap-y-12">
              {options.map((option) => (
                <Stack
                  key={option.value}
                  alignItems="center"
                  className="cursor-pointer"
                  direction="row"
                  gapX={8}
                  justifyContent="start"
                  role="button"
                  onClick={() => toggleProperty(option.value)}>
                  <Checkbox key={option.value} checked={value.includes(option.value)} />
                  <Typography variant="body-short-01">{option.label}</Typography>
                </Stack>
              ))}
            </Box>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

export default PropertiesToDuplicateSelector;
